// Copy for the /listing-photos-and-video marketing page.
//
// It lives here rather than inside the container for the same reason the FAQ
// does: src/constants/seo.js turns FAQS into the page's FAQPage JSON-LD and
// the page renders the same strings — one copy, so the structured data can
// never describe something the page doesn't say.
//
// Accuracy notes (every line below is something the product actually does):
//   * EDIT_KINDS mirrors the touch-up vocabulary the analysis can flag
//     (homelistingsbackend/photo_touchups.py EDIT_INSTRUCTIONS, and the same
//     labels the picker shows in TouchupPicker.js). No virtual staging: the
//     edit prompts forbid adding furniture or changing the property.
//   * Photo touch-ups are opt-in per photo, first redo free per photo,
//     failures refund. A video is one purchase however many clips it takes
//     (Peter, 2026-09-17), and its free redo is one per listing.
//   * Video clips are silent (audio is stripped) and are built from the
//     listing's own photos — never described as footage shot at the house,
//     and never as certified for any MLS or portal.
// Prices come from the credits catalog; nothing here hardcodes an amount.

import { ACTION_COSTS, FREE_MONTHLY_CREDITS, formatCreditsNumber } from "../constants/credits";

const n = formatCreditsNumber;

export const HERO = {
  title: "Listing photos and property video, your way",
  subtitle:
    "Prepare a listing to review and improve its photos, or create a video directly from your own photos without preparing a report. Choose your exact shots, their order, and landscape or vertical format. Watch and approve the video before sharing it.",
  ctaLabel: "Prepare a listing",
  ctaPath: "/verify",
  freeNote: `Every account includes ${n(FREE_MONTHLY_CREDITS)} credits a month, no card required.`,
};

// The improvements the photo review can name, in the product's own words.
export const EDIT_KINDS = [
  {
    title: "Declutter",
    body: "Clear movable clutter that pulls a buyer's eye away from the room.",
  },
  {
    title: "Floor marks",
    body: "Even out scuffs and marks the camera exaggerated on a floor.",
  },
  {
    title: "Yard debris",
    body: "Tidy the hoses, bins, and leaves sitting in front of the house.",
  },
  {
    title: "Green up the grass",
    body: "Bring a patchy or dormant lawn back to how the yard looks in season.",
  },
  {
    title: "Brighten to sunny",
    body: "Lift a flat, overcast exterior toward the light the house gets on a clear day, including the sky.",
  },
  {
    title: "Brighten the room",
    body: "Open up an interior shot that came out dark or muddy.",
  },
  {
    title: "Tidy the landscaping",
    body: "Neaten overgrown beds and edges around the entry.",
  },
];

export const PHOTO_STEPS = [
  {
    title: "Prepare the listing",
    body: "Start from a Zillow URL or enter the property yourself. The Listing Analysis reads the whole listing — pricing, description, and photos together.",
  },
  {
    title: "Read what the photo review found",
    body: "The review goes through every photo and names the ones that could look better, and what would help each. It is a list of suggestions, and it stops there.",
  },
  {
    title: "Check the photos you want changed",
    body: `You pick them yourself. Nothing is edited until you do, and the price — ${n(ACTION_COSTS.photo_touchup)} credits a photo — is shown before you start. Edits are usually ready within 15 minutes.`,
  },
  {
    title: "Compare them against the originals",
    body: "Each edited photo comes back beside the photo it came from, so you can look at both and keep whichever you prefer. The original is never overwritten.",
  },
];

export const VIDEO_STEPS = [
  {
    title: "Choose your photos and motion",
    body: `Upload photos, use an existing listing, or optionally import from Zillow. No report is required. Select up to six ordered photos: AI motion where suitable (the default) gives each photo that passes our safety check AI camera motion and the rest a gentle zoom, decided before generation; Photo motion only uses gentle zooms of the originals, with no AI. The whole-video price is ${n(ACTION_COSTS.video)} credits. Videos are silent: no music, no voiceover, no captions. Choose landscape or vertical format.`,
  },
  {
    title: "Watch the whole thing",
    body: "Watch the complete video and check its photos, order, framing, and every property detail. AI-motion checks can flag uncertainties; they never replace your review. Photo motion preserves the full view with borders where needed and a gentle zoom.",
  },
  {
    title: "Approve it, or ask again",
    body: "Nothing can be published or shared before you approve it. If the first cut isn't right, the first redo on a listing is free.",
  },
];

// Why this isn't the same purchase as a standalone photo-editing or
// reel-making app. Each row is a difference in how the work happens — not a
// claim about anyone else's quality.
export const COMPARISON = [
  {
    title: "The shortlist is made before you open it",
    body: "A separate app waits for you to upload a photo and choose an effect. Listino has already read the listing and named which photos are weak and what would help each one, so you are choosing from a shortlist instead of building one.",
  },
  {
    title: "One photo review, two answers",
    body: "The same photo review that recommends which shot should lead the listing and what order the rest should run in also names what to fix in each shot. You are not describing the property twice to two different apps.",
  },
  {
    title: "The photos are already here",
    body: "Photos arrive with the listing when you import it. Nothing to export, re-upload, rename, or download back into place.",
  },
  {
    title: "One balance covers both",
    body: "Touch-ups and video are priced in the same credits as everything else Listino prepares, on the plan you already have — not a subscription for photos and a second one for video.",
  },
  {
    title: "Nothing goes out without you",
    body: "Touch-ups happen photo by photo, only for the ones you check. A video waits on your approval. Listino publishes neither on its own.",
  },
];

export const HONESTY = {
  title: "What these edits are, and what they are not",
  body: "A touch-up improves how a real space already looks: light, a lawn, movable clutter. It does not add furniture that isn't in the room, change the property, or make a permanent condition disappear. A property video is a visual preview assembled from the listing's photos, not a recording of a walk through the house, and Listino does not certify a photo or a clip as approved for any MLS or portal. Disclosure rules for edited listing photos vary by MLS and by state and have been tightening — check yours with your broker before you publish.",
  linkLabel: "AI photo touch-ups vs virtual staging: what to disclose",
  linkPath: "/resources/ai-photo-editing-vs-virtual-staging",
};

export const PRICING_POINTS = [
  `A photo touch-up is ${n(ACTION_COSTS.photo_touchup)} credits, charged per photo you pick`,
  `A property video is ${n(ACTION_COSTS.video)} credits`,
  "The first redo of a photo is free, and so is the first redo of a video on a listing",
  "If something fails to prepare, the credits come back automatically",
  `Every account starts with ${n(FREE_MONTHLY_CREDITS)} credits a month, no card required`,
];

/** @type {{question: string, answer: string}[]} */
export const FAQS = [
  {
    question: "How much does a real estate photo touch-up cost?",
    answer: `${n(ACTION_COSTS.photo_touchup)} credits a photo, charged only for the photos you pick. Every account includes ${n(FREE_MONTHLY_CREDITS)} credits a month at no charge, the first redo of a photo is free, and if an edit fails the credits come back automatically.`,
  },
  {
    question: "Does Listino edit my listing photos automatically?",
    answer:
      "No. The photo review names the photos that could look better and what would help each one, and then stops. You check the ones to send, and each edited photo comes back beside the original so you can compare them.",
  },
  {
    question: "What can a photo touch-up change?",
    answer: `Improvements to the real space: ${EDIT_KINDS.map((k) => k.title.toLowerCase()).join(", ")}. It does not add furniture that isn't in the room, and it is not a way to hide a permanent condition.`,
  },
  {
    question: "Can I make a property video from listing photos?",
    answer: `Yes. Upload your own photos without preparing a report, or use photos from an existing listing. Choose up to six ordered shots, AI motion where suitable or photo motion only, and landscape or vertical format. The whole-video price is ${n(ACTION_COSTS.video)} credits. The video is silent and waits for your review before sharing.`,
  },
  {
    question: "Do I have to disclose AI-edited listing photos?",
    answer:
      "It depends on the edit and on where you list. Ordinary corrections are generally treated like traditional editing, while anything that could change how a buyer reads the property's condition should be labeled. Rules vary by MLS and by state and are tightening — confirm the wording with your broker.",
  },
  {
    question: "Do photos and video need their own subscription?",
    answer:
      "No. Both use your existing Listino credit balance. Photo touch-ups are available after the listing photo review; videos can start directly from uploaded photos without preparing a report.",
  },
];
